import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import SltQuestionEditor from "./SltQuestionEditor";
import ResourceEditor from "./ResourceEditor";
import TaskEditor from "./TaskEditor";
import PromptEditor from "./PromptEditor";
import { contentApi } from "./contentApi";
import "./content-editors.css";

const tabs = [
  ["slt", "SLT questions"],
  ["tasks", "Task templates"],
  ["resources", "Resources"],
  ["prompts", "Prompt versions"],
];

export default function ContentEditors() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("slt");
  const [customers, setCustomers] = useState([]);
  const [customerState, setCustomerState] = useState("loading");
  const [customerError, setCustomerError] = useState("");
  const [scope, setScope] = useState("global");
  const [dirty, setDirty] = useState(false);
  const [editorBusy, setEditorBusy] = useState(false);
  const [pendingTransition, setPendingTransition] = useState(null);
  const [error, setError] = useState("");
  const [live, setLive] = useState("");
  const generation = useRef(0);
  const customerAbort = useRef(null);

  async function loadCustomers() {
    const current = ++generation.current;
    customerAbort.current?.abort();
    const controller = new AbortController();
    customerAbort.current = controller;
    setCustomerState("loading");
    setCustomerError("");
    try {
      const rows = await contentApi("/admin/customers", {
        signal: controller.signal,
      });
      if (current !== generation.current) return;
      setCustomers(
        rows.filter(
          (row) => row.is_provider === false && row.status === "active",
        ),
      );
      setCustomerState("ready");
    } catch (e) {
      if (e.name === "AbortError" || current !== generation.current) return;
      setCustomerError(e.message);
      setCustomerState("error");
    }
  }

  useEffect(() => {
    loadCustomers();
    return () => {
      generation.current += 1;
      customerAbort.current?.abort();
    };
  }, []);

  function message(value) {
    setLive(value);
    setError("");
  }

  function requestTransition(next) {
    if (editorBusy) return;
    if (dirty) {
      setPendingTransition(next);
      return;
    }
    applyTransition(next);
  }

  function applyTransition(next) {
    setPendingTransition(null);
    setDirty(false);
    if (next.type === "tab") setTab(next.value);
    else if (next.type === "scope") setScope(next.value);
    else navigate("/admin");
  }

  const Editor =
    tab === "slt"
      ? SltQuestionEditor
      : tab === "tasks"
        ? TaskEditor
        : tab === "resources"
          ? ResourceEditor
          : PromptEditor;
  const scoped = tab === "slt" || tab === "resources";

  return (
    <section
      className="content-editors"
      aria-labelledby="content-editors-title"
    >
      <div className="content-page-heading">
        <div>
          <Link
            className="content-back-link"
            to="/admin"
            onClick={(event) => {
              event.preventDefault();
              requestTransition({ type: "back" });
            }}
            aria-disabled={editorBusy || undefined}
          >
            ← DI Console
          </Link>
          <h1 id="content-editors-title">Content editors</h1>
          <p>Manage the provider content banks used across InclusiveCore.</p>
        </div>
      </div>
      <div className="content-toolbar">
        <label>
          Editing scope
          <select
            value={scope}
            disabled={!scoped || editorBusy || customerState === "loading"}
            onChange={(event) =>
              requestTransition({ type: "scope", value: event.target.value })
            }
          >
            <option value="global">Global provider bank</option>
            {customers.map((customer) => (
              <option value={customer.id} key={customer.id}>
                {customer.name}
              </option>
            ))}
          </select>
        </label>
        {customerState === "loading" && (
          <span className="content-muted">Loading customer scopes…</span>
        )}
        {customerState === "error" && (
          <span className="content-scope-error" role="alert">
            {customerError}
            <button
              type="button"
              className="content-button secondary"
              onClick={loadCustomers}
            >
              Retry customer scopes
            </button>
          </span>
        )}
        {customerState === "ready" &&
          (scoped ? (
            <span className="content-muted">
              {scope === "global"
                ? "Global content is available to customers"
                : "Organisation content is visible only to this customer and provider staff"}
            </span>
          ) : (
            <span className="content-muted">Global provider bank</span>
          ))}
      </div>
      <nav className="content-tabs" aria-label="Content editor sections">
        {tabs.map(([id, label]) => (
          <button
            type="button"
            className={tab === id ? "active" : ""}
            aria-current={tab === id ? "page" : undefined}
            disabled={editorBusy}
            onClick={() => requestTransition({ type: "tab", value: id })}
            key={id}
          >
            {label}
          </button>
        ))}
      </nav>
      {error && (
        <p className="content-error" role="alert">
          {error}
        </p>
      )}
      <div className="sr-only" aria-live="polite">
        {live}
      </div>
      <Editor
        {...(scoped ? { scope } : {})}
        onMessage={message}
        onDirtyChange={setDirty}
        onBusyChange={setEditorBusy}
      />
      <AlertDialog
        open={Boolean(pendingTransition)}
        onOpenChange={(open) => !open && setPendingTransition(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Changing the editor section or scope will replace the current
              form. Your unsaved changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingTransition(null)}>
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => applyTransition(pendingTransition)}
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
